import React from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';

const styles = (theme: Theme) => ({
  root: {
    padding: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
      marginBottom: theme.spacing(3),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    height: 200,
    flexDirection: 'column' as const,
    gap: theme.spacing(2),
  },
  content: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(3),
    },
  },
});

interface Props extends WithStyles<typeof styles> {
  title?: string;
  titleIcon?: React.ReactNode;
  subtitle?: string | undefined;
  isLoading?: boolean;
  error?: string | null;
  children?: React.ReactNode;
}

const EditPageLayout = ({
  classes,
  title,
  titleIcon,
  subtitle,
  isLoading = false,
  error = null,
  children,
}: Props): React.JSX.Element => {
  if (isLoading) {
    return (
      <Container className={classes.root}>
        <Paper className={classes.paper}>
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body1">Loading...</Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className={classes.root}>
        <Paper className={classes.paper}>
          {title && (
            <Typography variant="h4" className={classes.header}>
              {titleIcon && (
                <Box component="span" sx={{ marginRight: 1, verticalAlign: 'middle' }}>
                  {titleIcon}
                </Box>
              )}
              {title}
            </Typography>
          )}
          <Alert severity="error">{error}</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container className={classes.root}>
      <Paper className={classes.paper}>
        {title && (
          <Typography variant="h4" className={classes.header}>
            {titleIcon && (
              <Box component="span" sx={{ marginRight: 1, verticalAlign: 'middle' }}>
                {titleIcon}
              </Box>
            )}
            {title}
          </Typography>
        )}

        {subtitle && (
          <Typography variant="body1" color="textSecondary" gutterBottom>
            {subtitle}
          </Typography>
        )}

        <div className={classes.content}>{children}</div>
      </Paper>
    </Container>
  );
};

export default withStyles(styles)(EditPageLayout);
